import React from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Checkbox,
  type SelectChangeEvent,
} from '@mui/material'
import type {
  GlobalIngestKey,
  IngestKeyDefaultStatus,
  UpdateGlobalIngestKeyPayload,
} from '../../types/ingest'

interface EditIngestKeyModalProps {
  open: boolean
  ingestKey: GlobalIngestKey | null
  onClose: () => void
  onSubmit: (id: string, payload: UpdateGlobalIngestKeyPayload) => Promise<unknown>
}

const DEFAULT_RATE_LIMIT = 10

const EditIngestKeyModal: React.FC<EditIngestKeyModalProps> = ({ open, ingestKey, onClose, onSubmit }) => {
  const [label, setLabel] = React.useState('')
  const [defaultStatus, setDefaultStatus] = React.useState<IngestKeyDefaultStatus>('published')
  const [allowAutoCategories, setAllowAutoCategories] = React.useState(true)
  const [rateLimit, setRateLimit] = React.useState<number>(DEFAULT_RATE_LIMIT)
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [serverError, setServerError] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!open || !ingestKey) {
      return
    }

    setLabel(ingestKey.label)
    setDefaultStatus(ingestKey.policy.default_status)
    setAllowAutoCategories(ingestKey.policy.allow_auto_create_categories)
    setRateLimit(ingestKey.policy.rate_limit_per_min ?? DEFAULT_RATE_LIMIT)
    setErrors({})
    setServerError(null)
    setSubmitting(false)
  }, [ingestKey, open])

  const validate = React.useCallback(() => {
    const nextErrors: Record<string, string> = {}

    if (!label.trim()) {
      nextErrors.label = 'A label is required.'
    }

    if (Number.isNaN(rateLimit) || rateLimit < 1 || rateLimit > 120) {
      nextErrors.rate_limit_per_min = 'Rate limit must be between 1 and 120.'
    }

    setErrors(nextErrors)

    return Object.keys(nextErrors).length === 0
  }, [label, rateLimit])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (submitting || !ingestKey) {
      return
    }

    if (!validate()) {
      return
    }

    setSubmitting(true)
    setServerError(null)

    const payload: UpdateGlobalIngestKeyPayload = {
      label: label.trim(),
      policy: {
        default_status: defaultStatus,
        allow_auto_create_categories: allowAutoCategories,
        rate_limit_per_min: Math.min(Math.max(Math.round(rateLimit), 1), 120),
      },
    }

    try {
      await onSubmit(ingestKey.id, payload)
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update ingest key.'
      setServerError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleStatusChange = (event: SelectChangeEvent<IngestKeyDefaultStatus>) => {
    setDefaultStatus(event.target.value as IngestKeyDefaultStatus)
  }

  const handleRateLimitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number.parseInt(event.target.value, 10)
    if (Number.isNaN(value)) {
      setRateLimit(NaN)
      return
    }
    setRateLimit(value)
  }

  const handleClose = () => {
    if (submitting) {
      return
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit ingest key</DialogTitle>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogContent dividers>
          <Stack spacing={3}>
            <TextField
              label="Label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              required
              autoFocus
              error={Boolean(errors.label)}
              helperText={errors.label}
              fullWidth
            />
            <FormControl fullWidth error={Boolean(errors.default_status)}>
              <InputLabel id="edit-ingest-key-default-status">Default status</InputLabel>
              <Select
                labelId="edit-ingest-key-default-status"
                value={defaultStatus}
                label="Default status"
                onChange={handleStatusChange}
              >
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
              {errors.default_status ? <FormHelperText>{errors.default_status}</FormHelperText> : null}
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowAutoCategories}
                  onChange={(event) => setAllowAutoCategories(event.target.checked)}
                />
              }
              label="Allow automatic creation of categories"
            />
            <TextField
              label="Rate limit per minute"
              type="number"
              inputProps={{ min: 1, max: 120 }}
              value={Number.isNaN(rateLimit) ? '' : rateLimit}
              onChange={handleRateLimitChange}
              error={Boolean(errors.rate_limit_per_min)}
              helperText={errors.rate_limit_per_min ?? 'Clamp between 1 and 120 requests per minute.'}
              fullWidth
            />
            {serverError ? <Alert severity="error">{serverError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save changes'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

export default EditIngestKeyModal
